import React, {
  useState,
  useContext,
  useEffect,
  createContext,
  useCallback,
  useRef
} from 'react';
import * as SDCCore from 'scandit-web-datacapture-core';
import * as SDCBarcode from 'scandit-web-datacapture-barcode';
import { toScanResult } from '@/lib/scanning/scanResult';
import {
  isScanDebugEnabled,
  recordCameraFailure,
  recordScan
} from '@/lib/scanning/scanDiagnostics';
import { SCAN_MODE, persistScanMode, readScanMode } from '@/lib/scanning/scanMode';
import { hasCameraApi, isCameraUnavailableError, isScanFirst } from '@/lib/scanning/scanFirst';
import {
  HANDOFF_ECHO_REARM_MS,
  handoffEchoFor,
  screenHandoffEcho
} from '@/lib/scanning/handoffEcho';
import ScanDiagnostics from '@/lib/shared-coms/ScanDiagnostics';
import useHardwareScanner, {
  shouldUseCaptureField
} from '@/lib/scanning/useHardwareScanner';
import { buzz, BUZZ_ACCEPTED } from '@/lib/scanning/feedback';
import './ScannerProvider.scss';

const licenseKey = import.meta.env.VITE_SCANDIT_API_KEY;

/**
 * Which kind of label the current scan target expects. The two need genuinely different
 * decoder settings: an item barcode is a fixed-length GS1 Code 128 / DataMatrix, while a zone
 * rack label is whatever the customer printed — usually a QR code, sometimes a short 1D
 * barcode that the item profile's symbol-count limits would silently refuse to decode.
 *
 * PRODUCT is the item profile plus the retail linear codes (UPC-A / EAN-13, UPC-E, EAN-8) an OTC
 * or UPC pack carries instead of a DataMatrix. It is used ONLY by the Pick → Verify → Ship
 * (Option 2) screens: an Rx pack prints a linear NDC-UPC beside its DataMatrix, and with UPC
 * decodable the camera — which takes whichever code it recognised first — would sometimes hand an
 * Option-1 serialized pick the linear code, a refusal it has never had. ITEM therefore stays as is.
 *
 * HOME is the item profile with Code 128 opened up past the 14/18-symbol GS1 lengths, so the
 * warehouse home screen can also read the picking slip's sales-order barcode (an 8-digit number,
 * or that number plus a split suffix). It is not the zone profile: rack labels are not what the
 * home screen is for, and enabling Code 39 / QR there would steal reads from a pack. It does add
 * the retail linear codes, for Product Lookup (scan a UPC → its zones, lots and quantities). The
 * cost is the one PRODUCT accepts: on an Rx pack the camera may read the NDC-UPC before the
 * DataMatrix, opening Product Lookup instead of the serialized item — aim at the 2D code alone.
 */
export const SCAN_PROFILE = {
  ITEM: 'item',
  ZONE: 'zone',
  PRODUCT: 'product',
  HOME: 'home'
};

/** Inclusive integer range, for symbology active symbol counts. */
const symbolCounts = (from, to) =>
  Array.from({ length: to - from + 1 }, (_, index) => from + index);

/**
 * Scandit's decode feedback with no sound and no vibration, for a scan target that asks for
 * `silentDecode`. Scandit otherwise beeps and vibrates on every decode, before the screen has
 * decided anything about the read — a screen that re-arms fast and ignores the pack still in frame
 * would beep several times a second while doing nothing.
 */
const silentDecodeFeedback = () => {
  const feedback = new SDCBarcode.BarcodeCaptureFeedback();
  feedback.success = new SDCCore.Feedback(null, null);
  return feedback;
};

/**
 * Scandit's default decode feedback — its beep, and a vibration where the device has one — played
 * on demand, for a `silentDecode` screen once it has accepted a camera read. Scandit plays its
 * sound through an HTML5 media element (one shared default sound), which an iPhone keeps audible
 * with the ring / silent switch on silent; the app's own Web Audio tones are muted there.
 */
const emitDecodeFeedback = () => {
  try {
    SDCBarcode.BarcodeCaptureFeedback.default.success.emit();
  } catch (error) {
    console.error('emitDecodeFeedback error:', error);
  }
};

// Create a context for the theme
const ScannerContext = createContext();

export const useScanner = () => useContext(ScannerContext);

export const ScannerProvider = ({ children }) => {
  const [scannerInstance, setScannerInstance] = useState();
  // There is exactly one camera pipeline for the whole app, but more than one component can
  // want it at a time — the zone-scan overlay opens on top of a page that is already scanning
  // items. Targets are therefore a stack rather than a single ref: the last one pushed owns
  // the camera, and popping it hands the camera straight back to the page underneath.
  const [scanTargets, setScanTargets] = useState([]);
  const activeTarget = scanTargets.length ? scanTargets[scanTargets.length - 1] : null;
  const scannerRef = activeTarget?.ref || null;
  // Which symbology profile the current owner needs — see the settings built in runScanner.
  const activeProfile = activeTarget?.profile || SCAN_PROFILE.ITEM;
  // Read by publishScan, which is built once and so cannot close over the current profile.
  const activeProfileRef = useRef(activeProfile);
  activeProfileRef.current = activeProfile;
  // Whether the current owner gives its own scan feedback (see silentDecodeFeedback). With no
  // owner, or one that does not ask, Scandit's default feedback is what plays.
  const activeSilentDecode = !!activeTarget?.silentDecode;
  const [scanResult, setScanResult] = useState(null);
  const barcodeCaptureRef = useRef(null);
  const reEnableTimeoutRef = useRef(null);
  const connectedElementRef = useRef(null);
  // Counts camera hand-offs. Releasing the camera takes several awaited SDK calls, and a screen
  // whose scanner opens as it loads takes the camera before the previous screen's release has
  // finished; a release checks this after every await and stops once a newer connect has begun,
  // so it never detaches or switches off the camera the new scanner was just given.
  const cameraHandoffRef = useRef(0);
  const settingsByProfileRef = useRef({});
  const appliedProfileRef = useRef(SCAN_PROFILE.ITEM);
  // The decode feedback last given to which BarcodeCapture: a freshly built one starts on the
  // default, so the comparison is per instance.
  const appliedFeedbackRef = useRef({ barcodeCapture: null, silent: false });
  // Set once the Scandit pipeline exists, so switching modes never rebuilds it.
  const cameraStartedRef = useRef(false);

  // `silentDecode`: this target plays its own feedback for a read, so Scandit's decode beep and
  // vibration are muted while it owns the camera (see the feedback effect below).
  const pushScanTarget = useCallback(
    (ref, profile = SCAN_PROFILE.ITEM, { silentDecode = false } = {}) => {
      if (!ref) return;
      setScanTargets((targets) => {
        const top = targets[targets.length - 1];
        // Returning the same array keeps a caller that pushes on every render from looping.
        if (
          top?.ref === ref &&
          top.profile === profile &&
          top.silentDecode === silentDecode
        )
          return targets;
        return [
          ...targets.filter((target) => target.ref !== ref),
          { ref, profile, silentDecode }
        ];
      });
    },
    []
  );

  const popScanTarget = useCallback((ref) => {
    if (!ref) return;
    setScanTargets((targets) => targets.filter((target) => target.ref !== ref));
  }, []);

  // Kept for callers that still think in terms of a single target. `null` means "tear the
  // whole stack down", which is what the provider's own cleanup paths want.
  const setScannerRef = useCallback(
    (ref) => (ref ? pushScanTarget(ref) : setScanTargets([])),
    [pushScanTarget]
  );

  // The last scan published (when, and whether a zone scanner took it), and the echo of it still
  // to be dropped after the camera last changed hands.
  const lastScanRef = useRef(null);
  const handoffEchoRef = useRef(null);

  /** Re-enables camera capture after `delayMs`, replacing any re-arm already scheduled. */
  const rearmCapture = (delayMs) => {
    if (reEnableTimeoutRef.current) clearTimeout(reEnableTimeoutRef.current);
    reEnableTimeoutRef.current = setTimeout(async () => {
      reEnableTimeoutRef.current = null;
      const bc = barcodeCaptureRef.current;
      if (bc && !bc.isEnabled()) await bc.setEnabled(true);
    }, delayMs);
  };

  /**
   * The single funnel every scan goes through, whatever read it. Consumers only ever see the
   * parsed result, so a page that works with the camera works with a handheld without knowing
   * one exists — `source` is there for diagnostics and for the on-screen indicator, not for
   * branching behaviour.
   */
  const publishScan = useCallback((raw, source) => {
    recordScan(raw, source);
    const result = toScanResult(raw, source);
    if (!result) return false;
    // A label still in frame from before the camera changed hands — see handoffEcho.ts. Dropped,
    // and the camera re-armed on the zone scanner's fast delay rather than the 3 s a failed read
    // waits: the operator is usually already aiming at the right label.
    const now = Date.now();
    if (source === 'camera') {
      const screened = screenHandoffEcho(handoffEchoRef.current, result.text, now);
      handoffEchoRef.current = screened.echo;
      if (screened.drop) {
        rearmCapture(HANDOFF_ECHO_REARM_MS);
        return true;
      }
    }
    lastScanRef.current = {
      text: result.text,
      zone: activeProfileRef.current === SCAN_PROFILE.ZONE,
      at: now
    };
    if (result.separatorsMissing) {
      // Worth knowing about: the field boundaries in this payload were reconstructed rather than
      // read, which means the scanner is dropping the FNC1 separators. See the DataWedge setup
      // notes in docs/handheld-scanning.md.
      console.warn(
        `[scanner] ${source} scan arrived without GS1 separators; field boundaries were inferred`
      );
    }
    setScanResult(result);
    return true;
  }, []);

  // The camera pipeline is built once in an effect that never re-runs, so its listener reaches
  // the handler through a ref rather than capturing the first render's closure.
  const publishScanRef = useRef(publishScan);
  publishScanRef.current = publishScan;

  // The echo of the last scan to drop once the camera has changed hands (see handoffEcho.ts).
  useEffect(() => {
    handoffEchoRef.current = scannerRef
      ? handoffEchoFor(
          lastScanRef.current,
          activeProfileRef.current === SCAN_PROFILE.ZONE,
          Date.now()
        )
      : null;
  }, [scannerRef]);

  // Read once: `?scandebug=1` persists itself, so the panel survives the navigation the very
  // first scan performs.
  const [scanDebug] = useState(isScanDebugEnabled);

  // Camera or laser. Seeded from the operator's saved choice and switchable at any time from
  // the scanning screen; see scanMode.ts for why these are modes rather than both at once.
  const [scanMode, setScanModeState] = useState(readScanMode);
  const setScanMode = useCallback((mode) => {
    persistScanMode(mode);
    setScanModeState(mode);
  }, []);

  // Whether the camera is known to be unusable: no camera support in this browser, permission
  // refused, or it failed to start. Cleared again if the operator grants the permission later from
  // the browser's settings.
  const [cameraBlocked, setCameraBlocked] = useState(() => !hasCameraApi());
  // The browser's answer for the camera permission, where it can be asked: with it 'granted', an
  // empty preview means the camera is still starting (the engine downloads on first use), not that
  // the operator has anything to allow.
  const [cameraPermission, setCameraPermission] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let permission: PermissionStatus | null = null;
    navigator.permissions
      ?.query({ name: 'camera' as PermissionName })
      .then((status) => {
        if (cancelled) return;
        permission = status;
        const sync = () => {
          setCameraPermission(status.state);
          // Never unblocks a browser that has no camera support at all.
          setCameraBlocked(!hasCameraApi() || status.state === 'denied');
        };
        sync();
        status.onchange = sync;
      })
      // Safari before 16 and some WebViews cannot be asked; a refusal then shows up as a failure
      // to start the camera instead.
      .catch(() => {});
    return () => {
      cancelled = true;
      if (permission) permission.onchange = null;
    };
  }, []);
  // Inputs that take a value by scanning or typing start on the scanner when this is true, on every
  // device — see scanFirst.ts. Laser mode never depends on the camera.
  const scanFirst = isScanFirst({ scanMode, cameraBlocked });

  const localHandleScan = async (text) => publishScanRef.current(text, 'camera');

  /**
   * A handheld scanner is listened for exactly while a scanning screen is open — the same
   * condition that owns the camera — so pulling the trigger and pointing the camera are two ways
   * of doing the identical thing on every screen that scans, and neither is listened for
   * anywhere else in the app.
   */
  const hardwareScanner = useHardwareScanner({
    enabled: scanTargets.length > 0,
    // Only laser mode needs a focused field for a wedge to commit into, and only a focused field
    // can raise a keyboard over the preview. Tying the two together removes both problems.
    useCaptureField: scanMode === SCAN_MODE.LASER && shouldUseCaptureField(),
    onScan: (scan) => {
      // A trigger pull gives no visual feedback of its own; confirm the app took it.
      buzz(BUZZ_ACCEPTED);
      publishScanRef.current(scan.data, 'hardware');
    }
  });

  const cleanupScanner = useCallback(() => {
    if (reEnableTimeoutRef.current) {
      clearTimeout(reEnableTimeoutRef.current);
      reEnableTimeoutRef.current = null;
    }
    connectedElementRef.current = null;
    if (scannerRef) setScannerRef(null);
    barcodeCaptureRef.current = null;
    if (scannerInstance?.barcodeCapture) {
      scannerInstance.barcodeCapture.setEnabled(false);
      scannerInstance.barcodeCapture.removeListener('didScan');
      scannerInstance.barcodeCapture.context.dispose();
      scannerInstance.barcodeCapture = null;
      setScannerInstance(null);
    }
    if (scannerInstance?.context) {
      // scannerInstance.context.dispose();
      scannerInstance.context.frameSource.switchToDesiredState(
        SDCCore.FrameSourceState.Off
      );
      // scannerContextRef.current = null;
    }
    // Let a remount build the pipeline again; the guard is a ref, so nothing else would reset it.
    cameraStartedRef.current = false;
  }, [scannerRef, scannerInstance]);

  const cleanupSettings = useCallback(async () => {
    if (reEnableTimeoutRef.current) {
      clearTimeout(reEnableTimeoutRef.current);
      reEnableTimeoutRef.current = null;
    }
    connectedElementRef.current = null;
    const handoff = cameraHandoffRef.current;

    // No setScannerRef(null) here: this only ever runs once the target stack is already empty
    // (see the effect below), and clearing it from a stale closure would yank the camera away
    // from a target that has since been pushed.
    if (scannerInstance?.barcodeCapture) {
      await scannerInstance.barcodeCapture.setEnabled(false);
    }
    if (cameraHandoffRef.current !== handoff) return;
    if (scannerInstance?.view) {
      scannerInstance.view.detachFromElement();
    }
    if (scannerInstance?.context) {
      await scannerInstance.context.frameSource.switchToDesiredState(
        SDCCore.FrameSourceState.Off
      );
    }
  }, [scannerInstance]);

  useEffect(() => {
    if (!scannerRef && !!scannerInstance?.barcodeCapture) cleanupSettings();
  }, [scannerRef, scannerInstance]);

  // Mirrors `scannerInstance` so the start/stop callbacks below can reach the live pipeline
  // without taking it as a dependency — they must not be rebuilt every time the instance changes.
  const scannerInstanceRef = useRef(null);
  scannerInstanceRef.current = scannerInstance;

  /**
   * Builds the Scandit pipeline. Guarded so it only ever happens once, however many times the
   * operator switches modes: the expensive part is the engine download and the context, and both
   * survive the camera being switched off.
   */
  const startCamera = useCallback(async () => {
    if (cameraStartedRef.current) return;
    cameraStartedRef.current = true;
    async function runScanner() {
      await SDCCore.configure({
        licenseKey: licenseKey,
        libraryLocation:
          'https://cdn.jsdelivr.net/npm/scandit-web-datacapture-barcode@6.x/build/engine/',
        moduleLoaders: [SDCBarcode.barcodeCaptureLoader()]
      });

      const context = await SDCCore.DataCaptureContext.create();

      const camera = SDCCore.Camera.default;
      await context.setFrameSource(camera);

      const settings = new SDCBarcode.BarcodeCaptureSettings();

      const SYMS = [
        SDCBarcode.Symbology.Code128,
        // SDCBarcode.Symbology.Code39,
        SDCBarcode.Symbology.QR,
        // SDCBarcode.Symbology.EAN8,
        // SDCBarcode.Symbology.UPCE,
        // SDCBarcode.Symbology.EAN13UPCA,
        SDCBarcode.Symbology.DataMatrix
      ];

      settings.enableSymbologies(SYMS);

      // Turn on inverted-color decoding for all enabled symbologies
      for (const sym of SYMS) {
        const s = settings.settingsForSymbology(sym);
        s.isColorInvertedEnabled = true;
      }

      const code128 = settings.settingsForSymbology(SDCBarcode.Symbology.Code128);
      code128.activeSymbolCounts = [14, 18];

      // Zone-label profile. Zone labels are printed by the customer, so nothing about their
      // length or symbology can be assumed — the item profile's Code 128 counts of exactly
      // 14/18 symbols would refuse a rack label reading "A-12" without any visible error.
      // Code 39 is enabled here (and only here) because it is the classic warehouse rack
      // symbology; it stays off for item scanning where it was deliberately disabled.
      const zoneSettings = new SDCBarcode.BarcodeCaptureSettings();
      const ZONE_SYMS = [
        SDCBarcode.Symbology.QR,
        SDCBarcode.Symbology.DataMatrix,
        SDCBarcode.Symbology.Code128,
        SDCBarcode.Symbology.Code39
      ];
      zoneSettings.enableSymbologies(ZONE_SYMS);
      for (const sym of ZONE_SYMS) {
        zoneSettings.settingsForSymbology(sym).isColorInvertedEnabled = true;
      }
      zoneSettings.settingsForSymbology(SDCBarcode.Symbology.Code128).activeSymbolCounts =
        symbolCounts(6, 40);
      zoneSettings.settingsForSymbology(SDCBarcode.Symbology.Code39).activeSymbolCounts =
        symbolCounts(3, 40);

      // Product profile (Pick → Verify → Ship screens only — see SCAN_PROFILE). Its own settings
      // object rather than a wider `settings`, so the item profile every Option-1 screen scans
      // with decodes exactly what it always has. The item symbologies keep the item profile's
      // Code 128 counts; the retail codes are fixed-length by definition and need no counts.
      const productSettings = new SDCBarcode.BarcodeCaptureSettings();
      const PRODUCT_SYMS = [
        ...SYMS,
        SDCBarcode.Symbology.EAN13UPCA,
        SDCBarcode.Symbology.UPCE,
        SDCBarcode.Symbology.EAN8
      ];
      productSettings.enableSymbologies(PRODUCT_SYMS);
      for (const sym of PRODUCT_SYMS) {
        productSettings.settingsForSymbology(sym).isColorInvertedEnabled = true;
      }
      productSettings.settingsForSymbology(SDCBarcode.Symbology.Code128).activeSymbolCounts = [
        14, 18
      ];

      // Home screen: the item symbologies, but Code 128 of the lengths a sales-order number
      // actually is. 14 and 18 stay inside the range, so a pack's GS1-128 still reads. Plus the
      // retail linear codes, for Product Lookup (scan a UPC → its zones, lots and quantities).
      const homeSettings = new SDCBarcode.BarcodeCaptureSettings();
      const HOME_SYMS = [
        ...SYMS,
        SDCBarcode.Symbology.EAN13UPCA,
        SDCBarcode.Symbology.UPCE,
        SDCBarcode.Symbology.EAN8
      ];
      homeSettings.enableSymbologies(HOME_SYMS);
      for (const sym of HOME_SYMS) {
        homeSettings.settingsForSymbology(sym).isColorInvertedEnabled = true;
      }
      homeSettings.settingsForSymbology(SDCBarcode.Symbology.Code128).activeSymbolCounts =
        symbolCounts(6, 20);

      settingsByProfileRef.current = {
        [SCAN_PROFILE.ITEM]: settings,
        [SCAN_PROFILE.ZONE]: zoneSettings,
        [SCAN_PROFILE.PRODUCT]: productSettings,
        [SCAN_PROFILE.HOME]: homeSettings
      };

      // const code39Settings = settings.settingsForSymbology(SDCBarcode.Symbology.Code39);
      // code39Settings.activeSymbolCounts = [
      //   7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20
      // ];

      const barcodeCapture = await SDCBarcode.BarcodeCapture.forContext(
        context,
        settings
      );
      await barcodeCapture.setEnabled(false);

      barcodeCapture.addListener({
        didScan: async (barcodeCapture, session) => {
          await barcodeCapture.setEnabled(false);
          const barcode = session.newlyRecognizedBarcodes[0];
          const scanSuccess = await localHandleScan(barcode.data);
          if (!scanSuccess)
            setTimeout(async () => {
              const bc = barcodeCaptureRef.current;
              if (bc) await bc.setEnabled(true);
            }, 3000);
        }
      });

      barcodeCaptureRef.current = barcodeCapture;
      setScannerInstance({ context, barcodeCapture, settings, camera });
    }

    await runScanner();
  }, []);

  /**
   * Releases the camera without dismantling the pipeline, so switching back is instant.
   *
   * On a rugged handheld this is the whole point of laser mode: the device's scan engine will
   * not fire while the browser holds the camera, so the trigger stays dead until the frame
   * source is actually off.
   */
  const stopCamera = useCallback(async () => {
    const instance = scannerInstanceRef.current;
    connectedElementRef.current = null;
    if (!instance) return;
    const handoff = cameraHandoffRef.current;
    if (instance.barcodeCapture) await instance.barcodeCapture.setEnabled(false);
    // Switched straight back to camera mode: the reconnect owns the camera now.
    if (cameraHandoffRef.current !== handoff) return;
    if (instance.view) instance.view.detachFromElement();
    if (instance.context?.frameSource) {
      await instance.context.frameSource.switchToDesiredState(SDCCore.FrameSourceState.Off);
    }
  }, []);

  // Unmount only. Kept separate from the mode effect below so that switching modes never runs
  // `cleanupScanner`, which tears down the scan-target stack every scanning screen depends on.
  // Reached through a ref because `cleanupScanner` closes over the instance it must dispose, and
  // a copy captured at mount would always be the empty one.
  const cleanupScannerRef = useRef(cleanupScanner);
  cleanupScannerRef.current = cleanupScanner;
  useEffect(() => () => cleanupScannerRef.current(), []);

  // Starting and stopping the camera is the entire difference between the two modes; everything
  // else — the wedge listener, the scan-target stack, the parsing — is common to both.
  useEffect(() => {
    if (scanMode === SCAN_MODE.CAMERA) {
      startCamera().catch((error) => {
        // Deliberately not an alert. A camera that will not start is already obvious on screen,
        // and a modal takes focus away from the hidden capture field — the one thing a handheld
        // needs in order to keep working when the camera does not.
        console.error('[scanner] camera unavailable; laser mode is unaffected', error);
        recordCameraFailure(error?.message || String(error));
        setCameraBlocked(true);
      });
    } else {
      stopCamera().catch((error) => console.error('[scanner] stopCamera failed', error));
    }
  }, [scanMode, startCamera, stopCamera]);

  // Swap decoder settings when the camera changes hands. Declared before connectToUI so the
  // right symbologies are in flight by the time the new owner's view is attached.
  useEffect(() => {
    const barcodeCapture = scannerInstance?.barcodeCapture;
    const settings = settingsByProfileRef.current[activeProfile];
    if (!barcodeCapture || !settings) return;
    if (appliedProfileRef.current === activeProfile) return;

    let cancelled = false;
    appliedProfileRef.current = activeProfile;
    (async () => {
      await barcodeCapture.applySettings(settings);
      // applySettings can land mid hand-off; only re-arm if something is still attached.
      if (!cancelled && connectedElementRef.current) await barcodeCapture.setEnabled(true);
    })().catch((error) => console.error('applyScanProfile error:', error));

    return () => {
      cancelled = true;
    };
  }, [activeProfile, scannerInstance]);

  // Mute or restore Scandit's decode feedback when the camera changes hands: silent exactly while
  // the owner asked for it, Scandit's default for every other owner and once the owner has gone.
  // Declared before connectToUI too, so a silent owner's first read is already silent.
  useEffect(() => {
    const barcodeCapture = scannerInstance?.barcodeCapture;
    if (!barcodeCapture) return;
    const applied = appliedFeedbackRef.current;
    const silentNow = applied.barcodeCapture === barcodeCapture && applied.silent;
    if (silentNow === activeSilentDecode) return;
    appliedFeedbackRef.current = { barcodeCapture, silent: activeSilentDecode };
    barcodeCapture
      .setFeedback(
        activeSilentDecode
          ? silentDecodeFeedback()
          : SDCBarcode.BarcodeCaptureFeedback.default
      )
      .catch((error) => console.error('applyDecodeFeedback error:', error));
  }, [activeSilentDecode, scannerInstance]);

  useEffect(() => {
    const connectToUI = async () => {
      // Laser mode owns the camera being off; `stopCamera` above has already released it.
      if (scanMode !== SCAN_MODE.CAMERA) return;
      if (
        !scannerRef?.current ||
        !scannerInstance ||
        !scannerInstance.barcodeCapture
      )
        return;

      // Already connected to this DOM element — skip
      if (connectedElementRef.current === scannerRef.current)
        return;

      // Any release still in flight stops here rather than undo this connect.
      cameraHandoffRef.current += 1;

      // Move the existing view to the new element rather than building another one. The
      // zone-scan overlay hands the camera back and forth many times a shift, and a fresh
      // DataCaptureView + overlay per hand-off is never released.
      if (scannerInstance.view) {
        scannerInstance.view.detachFromElement();
        scannerInstance.view.connectToElement(scannerRef.current);

        await scannerInstance.camera.switchToDesiredState(SDCCore.FrameSourceState.On);
        await scannerInstance.barcodeCapture.setEnabled(true);

        connectedElementRef.current = scannerRef.current;
        setCameraBlocked(false);
        return;
      }

      const view = await SDCCore.DataCaptureView.forContext(scannerInstance.context);
      view.connectToElement(scannerRef.current);
      view.addControl(new SDCCore.CameraSwitchControl());

      const overlay =
        await SDCBarcode.BarcodeCaptureOverlay.withBarcodeCaptureForViewWithStyle(
          scannerInstance.barcodeCapture,
          view,
          SDCBarcode.BarcodeCaptureOverlayStyle.Frame
        );

      const viewfinder = new SDCCore.RectangularViewfinder(
        SDCCore.RectangularViewfinderStyle.Square,
        SDCCore.RectangularViewfinderLineStyle.Light
      );

      await overlay.setViewfinder(viewfinder);

      await scannerInstance.camera.switchToDesiredState(SDCCore.FrameSourceState.On);
      await scannerInstance.barcodeCapture.setEnabled(true);

      connectedElementRef.current = scannerRef.current;
      setCameraBlocked(false);

      setScannerInstance((currentScannerInstance) => ({
        ...currentScannerInstance,
        overlay,
        view
      }));
    };

    connectToUI().catch((error) => {
      console.error('[scanner] could not start the camera', error);
      recordCameraFailure(error?.message || String(error));
      // Permission refused or no camera: screens fall back to their list or manual form first
      // (see scanFirst), and the scanner says why it is dark. A passing failure (the camera held
      // by another app for a moment, a scanner closed mid-start) leaves that alone — the next
      // connect simply tries again.
      if (isCameraUnavailableError(error)) setCameraBlocked(true);
    });
    // `scanMode` matters: coming back from laser mode, nothing else has changed, but the camera
    // is off and `connectedElementRef` was cleared, so the view has to be reconnected.
  }, [scannerRef, scannerInstance, scanMode]);

  // `delayMs` lets a caller that already told the operator what went wrong (the zone-scan
  // overlay) re-arm faster than the 3s an item scan uses to avoid double-reads of the same
  // label. Passing nothing keeps the original timing for every existing caller.
  const clearScanResult = useCallback((delayMs = 3000) => {
    setScanResult(null);
    rearmCapture(delayMs);
    // rearmCapture reads only refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drop an unconsumed scan WITHOUT scheduling a re-arm — for a component that is going away
  // and must not leave its zone label sitting in state for the page underneath to pick up.
  // Whoever inherits the camera re-enables capture through connectToUI anyway.
  const discardScanResult = useCallback(() => {
    if (reEnableTimeoutRef.current) {
      clearTimeout(reEnableTimeoutRef.current);
      reEnableTimeoutRef.current = null;
    }
    setScanResult(null);
  }, []);

  return (
    <ScannerContext.Provider
      value={{
        // scannerInitialized,
        scanResult,
        setScanResult: (result) => {
          if (result === null) clearScanResult();
          else setScanResult(result);
        },
        clearScanResult,
        discardScanResult,
        // For a `silentDecode` screen: Scandit's own beep for a camera read it has accepted.
        emitDecodeFeedback,
        setScannerRef,
        pushScanTarget,
        popScanTarget,
        // The ref object that currently owns the camera. Consumers compare their own ref
        // against it so only the topmost one reacts to a scan — otherwise the page behind the
        // zone-scan overlay would also try to handle the zone label as an item barcode.
        activeScanTarget: scannerRef,
        activeScanProfile: activeProfile,
        // Whether a handheld scanner is being listened for, and whether one has actually been
        // heard from. Screens use the second to tell the operator the trigger is live.
        hardwareScanner,
        // Which reader is live. Switching stops or starts the camera, which is what frees the
        // device's own scan engine or takes it back.
        scanMode,
        setScanMode,
        // Start inputs on the scanner rather than the list — see scanFirst.ts.
        scanFirst,
        // The camera permission was refused or the camera would not start.
        cameraBlocked,
        // The browser says the camera permission is granted.
        cameraGranted: cameraPermission === 'granted'
      }}
    >
      {children}
      {scanDebug && <ScanDiagnostics />}
    </ScannerContext.Provider>
  );
};
